import React, { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const EmailThreadModal = ({ leadId, leadName, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [subject, setSubject] = useState('RE: माता जी का संदेश — तुम्हारे लिए');
  const [includeLink, setIncludeLink] = useState(true);
  const [sending, setSending] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [toast, setToast] = useState('');

  const fetchThread = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/email-thread/${leadId}`);
      const d = await res.json();
      if (!d.error) setData(d);
    } catch (e) { console.error('Fetch email thread failed:', e); }
    setLoading(false);
  }, [leadId]);

  useEffect(() => { fetchThread(); }, [fetchThread]);

  const fmtDt = (iso) => iso ? new Date(iso).toLocaleString('hi-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

  const generateAIDraft = async () => {
    setDrafting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/ai-reply-draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_id: leadId, customer_message: '' }),
      });
      const d = await res.json();
      if (d.draft) setReply(d.draft);
      else setToast('Draft generation failed');
    } catch (e) { setToast('Error: ' + e.message); }
    setDrafting(false);
  };

  const sendReply = async () => {
    if (!reply.trim()) { setToast('Body खाली है'); return; }
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/send-manual-reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_id: leadId, subject, body: reply, include_payment_link: includeLink }),
      });
      const d = await res.json();
      if (d.success) {
        setToast('✓ भेज दिया');
        setReply('');
        await fetchThread();
      } else {
        setToast('Failed: ' + (d.error || 'unknown'));
      }
    } catch (e) { setToast('Error: ' + e.message); }
    setSending(false);
  };

  const togglePause = async () => {
    const newState = !data?.lead?.nurture_paused;
    await fetch(`${API_URL}/api/admin/pause-nurture/${leadId}?paused=${newState}`, { method: 'POST' });
    await fetchThread();
  };

  const lead = data?.lead || {};
  const thread = data?.thread || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" data-testid="email-thread-modal">
      <div className="bg-white w-full max-w-3xl max-h-[95vh] sm:max-h-[90vh] rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-700 to-indigo-700 text-white px-4 py-3 rounded-t-2xl flex items-center justify-between">
          <div>
            <div className="font-black text-lg" data-testid="thread-customer-name">📧 {leadName || lead.name}</div>
            <div className="text-xs opacity-90">{lead.email}</div>
          </div>
          <button onClick={onClose} className="text-white text-2xl font-bold hover:bg-white/20 rounded-lg w-9 h-9" data-testid="thread-close-btn">×</button>
        </div>

        {/* Status bar */}
        <div className="bg-gray-50 px-4 py-2 text-xs flex flex-wrap items-center gap-3 border-b">
          <span className={`px-2 py-1 rounded font-bold ${lead.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
            {lead.payment_status === 'paid' ? '✓ Paid' : '◷ Pending'}
          </span>
          <span className="text-gray-600">Nurture step: <b>{lead.nurture_step || 0}/5</b></span>
          {lead.last_customer_reply_at && (
            <span className="text-blue-600">💬 Last reply: {fmtDt(lead.last_customer_reply_at)}</span>
          )}
          <button onClick={togglePause} data-testid="toggle-nurture-btn"
            className={`ml-auto px-2 py-1 rounded font-bold ${lead.nurture_paused ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}>
            {lead.nurture_paused ? '▶ Resume' : '⏸ Pause Nurture'}
          </button>
        </div>

        {/* Thread */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50" data-testid="thread-messages">
          {loading && <div className="text-center text-gray-500">Loading...</div>}
          {!loading && thread.length === 0 && (
            <div className="text-center text-gray-500 py-8">अभी तक कोई बातचीत नहीं हुई</div>
          )}
          {thread.map((msg, idx) => {
            const isOut = msg.direction === 'out';
            return (
              <div key={idx} className={`flex ${isOut ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${idx}`}>
                <div className={`max-w-[88%] rounded-2xl p-3 shadow-sm ${isOut ? 'bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-300' : 'bg-white border border-gray-200'}`}>
                  <div className="flex items-center gap-2 mb-1 text-xs">
                    <span className={`font-bold ${isOut ? 'text-amber-700' : 'text-blue-700'}`}>
                      {isOut ? '🔮 माता जी' : '👤 ' + (lead.name || 'Customer')}
                    </span>
                    {isOut && msg.kind && (
                      <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-bold text-[10px]">
                        {msg.kind === 'ai_reply' ? 'AI' : msg.kind === 'manual_reply' ? 'MANUAL' : msg.kind.toUpperCase()}
                      </span>
                    )}
                    {isOut && msg.sent === false && (
                      <span className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded text-[10px] font-bold">FAILED</span>
                    )}
                    <span className="text-gray-500 ml-auto">{fmtDt(msg.ts)}</span>
                  </div>
                  {msg.subject && <div className="text-sm font-bold text-gray-700 mb-1">{msg.subject}</div>}
                  <div className="text-sm text-gray-800 whitespace-pre-wrap break-words">{msg.body}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Compose */}
        <div className="border-t p-3 bg-white" data-testid="thread-compose">
          <div className="mb-2 flex flex-wrap gap-2">
            <input type="text" value={subject} onChange={e => setSubject(e.target.value)}
              className="flex-1 min-w-[200px] px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:border-purple-400 outline-none"
              placeholder="Subject" data-testid="reply-subject-input" />
            <button onClick={generateAIDraft} disabled={drafting} data-testid="generate-ai-draft-btn"
              className="px-3 py-2 bg-purple-600 text-white text-sm font-bold rounded-lg hover:bg-purple-700 disabled:opacity-50">
              {drafting ? '...' : '✨ AI Draft'}
            </button>
          </div>
          <textarea value={reply} onChange={e => setReply(e.target.value)}
            placeholder="अपना जवाब यहाँ लिखें... (या ✨ AI Draft पर click करें)"
            className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:border-purple-400 outline-none resize-none"
            rows={5} data-testid="reply-body-input" />
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <label className="text-xs flex items-center gap-1 text-gray-600">
              <input type="checkbox" checked={includeLink} onChange={e => setIncludeLink(e.target.checked)}
                data-testid="include-payment-link-checkbox" />
              Auto-add Payment Link (₹5500)
            </label>
            <span className="text-xs text-gray-500 ml-auto">{toast}</span>
            <button onClick={sendReply} disabled={sending || !reply.trim()} data-testid="send-reply-btn"
              className="px-5 py-2 bg-emerald-600 text-white text-sm font-bold rounded-lg hover:bg-emerald-700 disabled:opacity-50">
              {sending ? 'भेज रहे हैं...' : '📤 Send Reply'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailThreadModal;
